import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiClient, API_PREFIX } from "@/lib/api";
import RecoveryPanel from "@/components/RecoveryPanel";

const API = API_PREFIX;
const ET_TZ = "America/New_York";
const DATA_SOURCE_MODE_OPTIONS = [
  { value: "zeus_only", label: "Zeus Only" },
  { value: "zeus_fallback_timescale", label: "Zeus + Timescale Fallback" },
  { value: "timescale_only", label: "Timescale Only" }
];
const LIVE_CURRENCY_OPTIONS = ["USD", "EUR", "CAD", "HKD", "CNY", "AUD", "INR"];
const LIVE_UNIT_OPTIONS = ["oz", "gram"];

const countLiveChartsPerMinute = (currencies = [], units = []) =>
  currencies.reduce((total, currency) => {
    const perUnit = currency === "USD" ? 45 : 35;
    return total + perUnit * units.length;
  }, 0);

const formatDateTime = (value, timeZone) => {
  if (!value) return "-";
  const date = new Date(value);
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(date);
};

const formatMs = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(0)} ms`;
};

export default function Jobs() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [deps, setDeps] = useState([]);
  const [query, setQuery] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [cronEnabled, setCronEnabled] = useState(true);
  const [cronInterval, setCronInterval] = useState(30);
  const [archiveTimeEt, setArchiveTimeEt] = useState("00:10");
  const [chartGranularity, setChartGranularity] = useState("1 minute");
  const [chartFallbacks, setChartFallbacks] = useState("3 minutes");
  const [chartDataSourceMode, setChartDataSourceMode] = useState("zeus_fallback_timescale");
  const [enabledCurrencies, setEnabledCurrencies] = useState(["USD", "EUR", "CAD", "HKD", "CNY", "AUD", "INR"]);
  const [enabledUnits, setEnabledUnits] = useState(["oz", "gram"]);
  const [supportedCurrencies, setSupportedCurrencies] = useState(LIVE_CURRENCY_OPTIONS);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingChart, setIsSavingChart] = useState(false);
  const [reportDays, setReportDays] = useState(30);
  const [reportSummary, setReportSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportPage, setReportPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(10);
  const [errorPage, setErrorPage] = useState(1);
  const [errorPageSize, setErrorPageSize] = useState(10);

  const buildRunParams = useCallback((overrides = {}) => {
    const currentPage = overrides.page ?? page;
    const currentPageSize = overrides.pageSize ?? pageSize;
    const currentQuery = overrides.query ?? query;
    const currentStart = overrides.start ?? start;
    const currentEnd = overrides.end ?? end;

    const params = {
      page: currentPage,
      page_size: currentPageSize
    };
    if (currentQuery.trim()) {
      params.q = currentQuery.trim();
    }
    if (currentStart) {
      params.start = new Date(currentStart).toISOString();
    }
    if (currentEnd) {
      params.end = new Date(currentEnd).toISOString();
    }
    return params;
  }, [end, page, pageSize, query, start]);

  const refreshStatusAndRuns = useCallback(async (overrides = {}) => {
    const currentPage = overrides.page ?? page;
    const currentPageSize = overrides.pageSize ?? pageSize;
    const params = buildRunParams(overrides);

    try {
      const [statusRes, runsRes] = await Promise.all([
        apiClient.get(`${API}/jobs/status`).catch(() => ({ data: null })),
        apiClient.get(`${API}/jobs/runs`, { params }).catch(() => ({ data: { runs: [], total: 0 } }))
      ]);

      if (statusRes.data) {
        setStatus(statusRes.data);
      }
      setRuns(runsRes.data.runs || []);
      setTotal(runsRes.data.total ?? 0);
      setPage(runsRes.data.page ?? currentPage);
      setPageSize(runsRes.data.page_size ?? currentPageSize);
    } catch (error) {
      // ignore polling errors
    }
  }, [buildRunParams, page, pageSize]);

  const loadData = useCallback(async (overrides = {}) => {
    const currentPage = overrides.page ?? page;
    const currentPageSize = overrides.pageSize ?? pageSize;

    setLoading(true);
    try {
      const params = buildRunParams(overrides);
      const currentReportDays = overrides.reportDays ?? reportDays;
      const [statusRes, runsRes, configRes, chartConfigRes, depsRes, reportsRes, currenciesRes] = await Promise.all([
        apiClient.get(`${API}/jobs/status`).catch(() => ({ data: null })),
        apiClient.get(`${API}/jobs/runs`, { params }).catch(() => ({ data: { runs: [], total: 0 } })),
        apiClient.get(`${API}/jobs/config`).catch(() => ({ data: null })),
        apiClient.get(`${API}/jobs/chart-config`).catch(() => ({ data: null })),
        apiClient.get(`${API}/health/dependencies`).catch(() => ({ data: { dependencies: [] } })),
        apiClient
          .get(`${API}/jobs/reports/daily`, { params: { days: currentReportDays } })
          .catch(() => ({ data: { summary: null, reports: [] } })),
        apiClient.get(`${API}/currencies`).catch(() => ({ data: null }))
      ]);

      if (statusRes.data) {
        setStatus(statusRes.data);
        setCronEnabled(statusRes.data.cron_enabled);
        setCronInterval(statusRes.data.interval_seconds);
        setArchiveTimeEt(statusRes.data.archive_time_et || "00:10");
      }
      if (configRes.data) {
        setCronEnabled(configRes.data.cron_enabled);
        setCronInterval(configRes.data.interval_seconds);
        setArchiveTimeEt(configRes.data.archive_time_et || "00:10");
      }
      if (currenciesRes?.data?.supported?.length) {
        setSupportedCurrencies(currenciesRes.data.supported);
      }
      if (chartConfigRes.data) {
        setChartGranularity(chartConfigRes.data.granularity || "1 minute");
        const fallback = chartConfigRes.data.fallback_granularities || [];
        setChartFallbacks(Array.isArray(fallback) ? fallback.join(", ") : String(fallback));
        setChartDataSourceMode(chartConfigRes.data.data_source_mode || "zeus_fallback_timescale");
        setEnabledCurrencies(chartConfigRes.data.enabled_currencies || LIVE_CURRENCY_OPTIONS);
        setEnabledUnits(chartConfigRes.data.enabled_units || LIVE_UNIT_OPTIONS);
      }
      setDeps(depsRes.data.dependencies || []);
      setRuns(runsRes.data.runs || []);
      setTotal(runsRes.data.total ?? 0);
      setPage(runsRes.data.page ?? currentPage);
      setPageSize(runsRes.data.page_size ?? currentPageSize);
      setReportSummary(reportsRes.data.summary || null);
      setReports(reportsRes.data.reports || []);
      setReportDays(currentReportDays);
    } catch (error) {
      toast.error("Failed to load job status");
    }
    setLoading(false);
  }, [buildRunParams, page, pageSize, reportDays]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      refreshStatusAndRuns();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadData, refreshStatusAndRuns]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiClient.post(`${API}/jobs/config`, {
        cron_enabled: cronEnabled,
        interval_seconds: Number(cronInterval),
        archive_time_et: archiveTimeEt
      });
      await loadData();
      toast.success("Cron config updated");
    } catch (error) {
      toast.error("Failed to update cron config");
    }
    setIsSaving(false);
  };

  const handleSaveChartConfig = async () => {
    setIsSavingChart(true);
    try {
      const fallbacks = chartFallbacks
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      await apiClient.post(`${API}/jobs/chart-config`, {
        granularity: chartGranularity,
        fallback_granularities: fallbacks,
        data_source_mode: chartDataSourceMode,
        enabled_currencies: enabledCurrencies,
        enabled_units: enabledUnits
      });
      await loadData();
      toast.success("Chart config updated");
    } catch (error) {
      toast.error("Failed to update chart config");
    }
    setIsSavingChart(false);
  };

  const handleApplyFilters = async () => {
    await loadData({ page: 1 });
  };

  const handleClearFilters = async () => {
    setQuery("");
    setStart("");
    setEnd("");
    await loadData({ page: 1, query: "", start: "", end: "" });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const estimatedLiveChartsPerMinute = countLiveChartsPerMinute(enabledCurrencies, enabledUnits);
  const fromItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const toItem = Math.min(total, page * pageSize);
  const reportTotal = reports.length;
  const reportTotalPages = Math.max(1, Math.ceil(reportTotal / reportPageSize));
  const activeReportPage = Math.min(reportPage, reportTotalPages);
  const reportFromItem = reportTotal === 0 ? 0 : (activeReportPage - 1) * reportPageSize + 1;
  const reportToItem = Math.min(reportTotal, activeReportPage * reportPageSize);
  const pagedReports = reports.slice((activeReportPage - 1) * reportPageSize, activeReportPage * reportPageSize);
  const errorSamples = reports
    .flatMap((row) =>
      (row.error_samples || []).map((sample) => ({
        ...sample,
        day: row.day,
        job_type: row.job_type
      }))
    )
    .sort((left, right) => {
      const leftAt = left.at ? new Date(left.at).getTime() : 0;
      const rightAt = right.at ? new Date(right.at).getTime() : 0;
      return rightAt - leftAt;
    });
  const errorTotal = errorSamples.length;
  const errorTotalPages = Math.max(1, Math.ceil(errorTotal / errorPageSize));
  const activeErrorPage = Math.min(errorPage, errorTotalPages);
  const errorFromItem = errorTotal === 0 ? 0 : (activeErrorPage - 1) * errorPageSize + 1;
  const errorToItem = Math.min(errorTotal, activeErrorPage * errorPageSize);
  const pagedErrorSamples = errorSamples.slice(
    (activeErrorPage - 1) * errorPageSize,
    activeErrorPage * errorPageSize
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-zinc-600 border-t-zinc-100 rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="jobs-page" className="min-h-screen bg-[#09090B] p-4 md:p-6">
      <header className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-100">Jobs & Scheduler</h1>
        <p className="text-zinc-400 text-sm mt-1">Queue status, cron configuration, and chart generation settings</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
        <div className="col-span-1 md:col-span-4 bg-[#18181B] border border-zinc-800 rounded-md p-4">
          <h3 className="text-lg text-zinc-100 font-semibold mb-3">Queue Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Queued</span>
              <span className="text-zinc-100 font-mono text-sm">{status?.queue_length ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Archive Queue</span>
              <span className="text-zinc-100 font-mono text-sm">{status?.archive_queue_length ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Running</span>
              <span className="text-zinc-100 font-mono text-sm">{status?.running ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Failed</span>
              <span className="text-zinc-100 font-mono text-sm">{status?.failed ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Queue Mode</span>
              <span className="text-zinc-100 font-mono text-xs">{status?.queue_mode || "-"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Last Base Run</span>
              <span className="text-zinc-100 font-mono text-xs">
                {status?.last_base_run
                  ? `${formatDateTime(status.last_base_run, "UTC")} UTC / ${formatDateTime(
                      status.last_base_run,
                      ET_TZ
                    )} ET`
                  : "-"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Last Archive</span>
              <span className="text-zinc-100 font-mono text-xs">
                {status?.last_archive_run
                  ? `${formatDateTime(status.last_archive_run, "UTC")} UTC / ${formatDateTime(
                      status.last_archive_run,
                      ET_TZ
                    )} ET`
                  : "-"}
              </span>
            </div>
            {(status?.live_queue_depths || []).length > 0 && (
              <div className="pt-2 border-t border-zinc-800">
                <div className="text-zinc-400 text-xs mb-2">Live Queues</div>
                <div className="space-y-1">
                  {(status?.live_queue_depths || []).map((row) => (
                    <div key={row.currency} className="flex items-center justify-between gap-3">
                      <span className="text-zinc-500 text-xs">{row.currency}</span>
                      <span className="text-zinc-200 font-mono text-xs">{row.depth}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-span-1 md:col-span-4 bg-[#18181B] border border-zinc-800 rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg text-zinc-100 font-semibold">Dependencies</h3>
            <button
              onClick={loadData}
              className="text-xs text-zinc-400 hover:text-zinc-200"
            >
              Refresh
            </button>
          </div>
          <div className="space-y-3">
            {deps.length === 0 && (
              <div className="text-zinc-500 text-sm">No health data</div>
            )}
            {deps.map((dep) => (
              <div key={dep.name} className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-zinc-200 text-sm">{dep.name}</div>
                  {dep.message && (
                    <div className="text-zinc-500 text-xs">{dep.message}</div>
                  )}
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded border ${
                    dep.status === "ok"
                      ? "border-emerald-500/40 text-emerald-400"
                      : dep.status === "disabled"
                      ? "border-zinc-600 text-zinc-400"
                      : "border-red-500/40 text-red-400"
                  }`}
                >
                  {dep.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-1 md:col-span-4 bg-[#18181B] border border-zinc-800 rounded-md p-4">
          <h3 className="text-lg text-zinc-100 font-semibold mb-1">Cron Config</h3>
          <p className="text-zinc-500 text-xs mb-3">
            Archive scheduling is evaluated in ET. Daily archive snapshots target the previous ET date at 23:59.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Enabled</span>
              <input
                type="checkbox"
                checked={cronEnabled}
                onChange={(e) => setCronEnabled(e.target.checked)}
                className="h-4 w-4 accent-emerald-500"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-400 text-sm">Interval (sec)</span>
              <input
                type="number"
                min="10"
                value={cronInterval}
                onChange={(e) => setCronInterval(e.target.value)}
                className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs w-28 text-right"
                title="Scheduler tick interval in seconds"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-zinc-400 text-sm">Archive Trigger (ET)</div>
                  <div className="text-zinc-500 text-xs">
                    Snapshot target: previous day {status?.archive_target_time_et || "23:59"} ET
                  </div>
                </div>
              <input
                type="time"
                value={archiveTimeEt}
                onChange={(e) => setArchiveTimeEt(e.target.value)}
                className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
                title="Set the daily archive trigger time in ET"
              />
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-md px-3 py-2 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save Cron Config"}
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <RecoveryPanel />
      </div>

      <div className="bg-[#18181B] border border-zinc-800 rounded-md p-4 mb-6">
        <h3 className="text-lg text-zinc-100 font-semibold mb-1">Chart Generation Config</h3>
        <p className="text-zinc-500 text-xs mb-3">
          Granularity controls the scheduled intraday bucket size. Worker source mode decides whether scheduled jobs use Zeus directly, Zeus with Timescale fallback, or Timescale only.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-zinc-400 text-sm block mb-1">Primary Granularity</label>
            <input
              type="text"
              value={chartGranularity}
              onChange={(e) => setChartGranularity(e.target.value)}
              className="w-full bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
              placeholder="1 minute"
            />
            <p className="text-zinc-500 text-xs mt-1">Examples: `1 minute`, `3 minutes`, `4 hours`.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-zinc-400 text-sm block mb-1">Fallback Granularities (comma-separated)</label>
            <input
              type="text"
              value={chartFallbacks}
              onChange={(e) => setChartFallbacks(e.target.value)}
              className="w-full bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
              placeholder="1 minute, 5 minutes"
            />
            <p className="text-zinc-500 text-xs mt-1">
              Used when the primary granularity returns insufficient data.
            </p>
          </div>
        </div>
        <div className="mt-4">
          <label className="text-zinc-400 text-sm block mb-1">Data Source Mode</label>
          <select
            value={chartDataSourceMode}
            onChange={(e) => setChartDataSourceMode(e.target.value)}
            className="w-full md:w-[360px] bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-2 text-sm"
          >
            {DATA_SOURCE_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-zinc-500 text-xs mt-1">
            This affects scheduler and worker jobs only. Manual dashboard generation still uses its own direct source selector.
          </p>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-400 text-sm block mb-2">Enabled Currencies</label>
            <div className="flex flex-wrap gap-2">
              {supportedCurrencies.map((currency) => {
                const checked = enabledCurrencies.includes(currency);
                return (
                  <label
                    key={currency}
                    className={`px-2 py-1 rounded-md border text-xs cursor-pointer ${
                      checked
                        ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                        : "border-zinc-700 text-zinc-400"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() =>
                        setEnabledCurrencies((current) =>
                          checked
                            ? current.filter((item) => item !== currency)
                            : [...current, currency]
                        )
                      }
                    />
                    {currency}
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-sm block mb-2">Enabled Units</label>
            <div className="flex flex-wrap gap-2">
              {LIVE_UNIT_OPTIONS.map((unit) => {
                const checked = enabledUnits.includes(unit);
                return (
                  <label
                    key={unit}
                    className={`px-2 py-1 rounded-md border text-xs cursor-pointer ${
                      checked
                        ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                        : "border-zinc-700 text-zinc-400"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() =>
                        setEnabledUnits((current) =>
                          checked
                            ? current.filter((item) => item !== unit)
                            : [...current, unit]
                        )
                      }
                    />
                    {unit}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-4 bg-zinc-900 border border-zinc-800 rounded-md p-3">
          <div className="text-zinc-500 text-xs">Effective Live Matrix</div>
          <div className="text-zinc-100 text-sm mt-1">
            {enabledCurrencies.length} currencies, {enabledUnits.length} units, about {estimatedLiveChartsPerMinute} child charts per minute
          </div>
          <div className="text-zinc-500 text-xs mt-1">
            USD renders 90 charts/minute at two units. Each non-USD currency renders 70 charts/minute at two units.
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveChartConfig}
            disabled={isSavingChart}
            className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-md px-3 py-2 disabled:opacity-50"
          >
            {isSavingChart ? "Saving..." : "Save Chart Config"}
          </button>
        </div>
      </div>

      <div className="bg-[#18181B] border border-zinc-800 rounded-md p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg text-zinc-100 font-semibold">Historical Daily Report</h3>
            <p className="text-zinc-500 text-xs mt-1">
              Compact long-term health/performance summary from aggregated runs.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-zinc-400 text-xs">Window</label>
            <select
              value={reportDays}
              onChange={(e) => {
                setReportPage(1);
                setErrorPage(1);
                loadData({ reportDays: Number(e.target.value), page: 1 });
              }}
              className="bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
            >
              <option value={7}>7d</option>
              <option value={30}>30d</option>
              <option value={90}>90d</option>
              <option value={365}>365d</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3">
            <div className="text-zinc-500 text-xs">Total Runs</div>
            <div className="text-zinc-100 text-xl font-semibold">{reportSummary?.total_runs ?? 0}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3">
            <div className="text-zinc-500 text-xs">Error Rate</div>
            <div className="text-zinc-100 text-xl font-semibold">
              {reportSummary?.error_rate_percent ?? 0}%
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3">
            <div className="text-zinc-500 text-xs">Avg Latency</div>
            <div className="text-zinc-100 text-xl font-semibold">
              {formatMs(reportSummary?.avg_latency_ms)}
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3">
            <div className="text-zinc-500 text-xs">Error Logs (Sampled)</div>
            <div className="text-zinc-100 text-xl font-semibold">{reportSummary?.error_logs ?? 0}</div>
          </div>
        </div>

        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="text-zinc-500">
              <tr className="text-left border-b border-zinc-800">
                <th className="py-2">Day</th>
                <th className="py-2">Job Type</th>
                <th className="py-2">Runs</th>
                <th className="py-2">Error Rate</th>
                <th className="py-2">Avg / P95</th>
                <th className="py-2">Max</th>
                <th className="py-2">Errors</th>
              </tr>
            </thead>
            <tbody className="text-zinc-200">
              {reports.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-3 text-zinc-500 text-xs">No report data yet</td>
                </tr>
              )}
              {pagedReports.map((row) => (
                <tr key={row.report_id} className="border-b border-zinc-800/50">
                  <td className="py-2 text-xs font-mono">{row.day}</td>
                  <td className="py-2 text-xs">{row.job_type}</td>
                  <td className="py-2 text-xs">{row.total_runs}</td>
                  <td className="py-2 text-xs">{row.error_rate_percent}%</td>
                  <td className="py-2 text-xs">
                    {formatMs(row.avg_latency_ms)} / {formatMs(row.p95_latency_ms_estimate)}
                  </td>
                  <td className="py-2 text-xs">{formatMs(row.latency_max_ms)}</td>
                  <td className="py-2 text-xs">{(row.error_samples || []).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mb-4 flex items-center justify-between text-xs text-zinc-500">
          <div>
            Showing {reportFromItem}-{reportToItem} of {reportTotal}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={reportPageSize}
              onChange={(e) => {
                setReportPageSize(Number(e.target.value));
                setReportPage(1);
              }}
              className="bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <button
              onClick={() => setReportPage(Math.max(1, activeReportPage - 1))}
              disabled={activeReportPage <= 1}
              className="px-2 py-1 rounded border border-zinc-700 text-zinc-300 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              Page {activeReportPage} / {reportTotalPages}
            </span>
            <button
              onClick={() => setReportPage(Math.min(reportTotalPages, activeReportPage + 1))}
              disabled={activeReportPage >= reportTotalPages}
              className="px-2 py-1 rounded border border-zinc-700 text-zinc-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>

        <div>
          <h4 className="text-zinc-300 text-sm font-medium mb-2">Recent Error Samples</h4>
          <div className="space-y-2">
            {pagedErrorSamples.map((sample, idx) => (
                <div key={`${sample.job_id}-${sample.at}-${idx}`} className="bg-zinc-900 border border-zinc-800 rounded-md p-2">
                  <div className="text-zinc-400 text-xs font-mono">
                    {sample.day} {sample.job_type} {sample.job_id}
                  </div>
                  <div className="text-zinc-500 text-xs mb-1">
                    {sample.at ? `${formatDateTime(sample.at, "UTC")} UTC` : "-"}
                  </div>
                  <div className="text-red-400 text-xs break-words">{sample.message}</div>
                </div>
              ))}
            {reports.length > 0 && errorTotal === 0 && (
                <div className="text-zinc-500 text-xs">No recent sampled errors in this window.</div>
              )}
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
            <div>
              Showing {errorFromItem}-{errorToItem} of {errorTotal}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={errorPageSize}
                onChange={(e) => {
                  setErrorPageSize(Number(e.target.value));
                  setErrorPage(1);
                }}
                className="bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                onClick={() => setErrorPage(Math.max(1, activeErrorPage - 1))}
                disabled={activeErrorPage <= 1}
                className="px-2 py-1 rounded border border-zinc-700 text-zinc-300 disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                Page {activeErrorPage} / {errorTotalPages}
              </span>
              <button
                onClick={() => setErrorPage(Math.min(errorTotalPages, activeErrorPage + 1))}
                disabled={activeErrorPage >= errorTotalPages}
                className="px-2 py-1 rounded border border-zinc-700 text-zinc-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#18181B] border border-zinc-800 rounded-md p-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg text-zinc-100 font-semibold">Recent Runs</h3>
            <p className="text-zinc-500 text-xs mt-1">
              Filter by time range, status, job type, or job id (times displayed in UTC and ET).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search job id, type, status"
              className="bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs w-60"
            />
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
              title="Filter start time (your local time; converted to UTC)"
            />
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
              title="Filter end time (your local time; converted to UTC)"
            />
            <select
              value={pageSize}
              onChange={(e) => {
                const nextSize = Number(e.target.value);
                setPageSize(nextSize);
                loadData({ page: 1, pageSize: nextSize });
              }}
              className="bg-zinc-900 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button
              onClick={handleApplyFilters}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-md px-3 py-1"
            >
              Apply
            </button>
            <button
              onClick={handleClearFilters}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium rounded-md px-3 py-1 border border-zinc-700"
            >
              Clear
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-zinc-500">
              <tr className="text-left border-b border-zinc-800">
                <th className="py-2">Job</th>
                <th className="py-2">Status</th>
                <th className="py-2">Created (UTC / ET)</th>
                <th className="py-2">Completed (UTC / ET)</th>
                <th className="py-2">Error</th>
              </tr>
            </thead>
            <tbody className="text-zinc-200">
              {runs.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-4 text-zinc-500">No runs yet</td>
                </tr>
              )}
              {runs.map((run) => (
                <tr key={run.job_id} className="border-b border-zinc-800/50">
                  <td className="py-2 font-mono text-xs">{run.job_type}</td>
                  <td className="py-2">
                    <div className="flex flex-col gap-1">
                      <span className={`w-fit text-xs px-2 py-0.5 rounded border ${run.status === 'success' ? 'border-emerald-500/40 text-emerald-400' : run.status === 'failed' ? 'border-red-500/40 text-red-400' : 'border-zinc-600 text-zinc-300'}`}>
                        {run.status}
                      </span>
                      {run.meta?.final_state && run.meta.final_state !== run.status && (
                        <span className="text-[11px] text-zinc-500">{run.meta.final_state}</span>
                      )}
                      {run.job_type === "generate_intraday_currency" && (
                        <span className="text-[11px] text-zinc-500">
                          {Number(run.completed_children || 0)}/{Number(run.expected_children || 0)} complete
                          {Number(run.skipped_children || 0) > 0 ? `, ${run.skipped_children} skipped` : ""}
                          {Number(run.failed_children || 0) > 0 ? `, ${run.failed_children} failed` : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 text-xs">
                    {run.created_at
                      ? `${formatDateTime(run.created_at, "UTC")} / ${formatDateTime(run.created_at, ET_TZ)}`
                      : "-"}
                  </td>
                  <td className="py-2 text-xs">
                    {run.completed_at
                      ? `${formatDateTime(run.completed_at, "UTC")} / ${formatDateTime(run.completed_at, ET_TZ)}`
                      : "-"}
                  </td>
                  <td className="py-2 text-xs text-red-400 truncate max-w-[240px]">{run.error_message || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
          <div>
            Showing {fromItem}-{toItem} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadData({ page: Math.max(1, page - 1) })}
              disabled={page <= 1}
              className="px-2 py-1 rounded border border-zinc-700 text-zinc-300 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              Page {page} / {totalPages}
            </span>
            <button
              onClick={() => loadData({ page: Math.min(totalPages, page + 1) })}
              disabled={page >= totalPages}
              className="px-2 py-1 rounded border border-zinc-700 text-zinc-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
